import {
  Container,
  Grid,
  MantineProvider,
  MediaQuery,
  Paper,
  Stepper,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { X } from 'tabler-icons-react';
import ActivationStep from '../../components/merchant-onboarding/activation-step/activation-step.component';
import { RetrieveOnboardingAutofillDataResponse } from '../../components/merchant-onboarding/activation-step/interfaces/retrieve-onboarding-autofill.types';
import BusinessFormStep from '../../components/merchant-onboarding/business-form-step/business-form-step.component';
import BusinessTypeStep from '../../components/merchant-onboarding/business-type-step/business-type-step.component';
import GeneralInformationStep from '../../components/merchant-onboarding/general-info-step/general-info-step.component';
import StatementAcceptanceStep from '../../components/merchant-onboarding/statement-acceptance-step/statement-acceptance-step.component';
import { OnboardFormInitialValues } from '../../data/onboard-form-initival-values.object';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { ErrorResponse } from '../../type/error-response.types';
import { MerchantOnboardingFormValues } from '../../type/merchant-onboarding-form-values.types';
import './styles/merchant-onboarding.styles.css';

function ActivatePage() {
  const [active, setActive] = useState(0);
  const nextStep = () =>
    setActive((current) => (current < 5 ? current + 1 : current));
  const prevStep = () =>
    setActive((current) => (current > 0 ? current - 1 : current));
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<MerchantOnboardingFormValues>({
    initialValues: OnboardFormInitialValues,
  });
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  useEffect(() => {
    const retrieveAutofillData = async () => {
      try {
        setIsLoading(true);
        const response = await axiosPrivate.get(
          `/web/onboard/${auth?.id}/autofill`
        );

        const autofillData: RetrieveOnboardingAutofillDataResponse =
          response.data;

        form.setValues({
          ...form.values,
          merchantName:
            form.values.merchantName.trim().length > 1
              ? form.values.merchantName
              : autofillData.merchantName ?? '',
          aFirstName:
            form.values.aFirstName.trim().length > 1
              ? form.values.aFirstName
              : autofillData.representative.authorized.firstName,
          aMiddleName:
            form.values.aMiddleName.trim().length > 1
              ? form.values.aMiddleName
              : autofillData.representative.authorized.middleName ?? '',
          isAMNameDisabled:
            form.values.aMiddleName === 'N/A' ||
            autofillData.representative.authorized.middleName === 'N/A' ||
            form.values.aMiddleName === '.' ||
            autofillData.representative.authorized.middleName === '.',

          aLastName:
            form.values.aLastName.trim().length > 1
              ? form.values.aLastName
              : autofillData.representative.authorized.lastName,
          aEmailAddress:
            form.values.aEmailAddress.trim().length > 1
              ? form.values.aEmailAddress
              : autofillData.representative.authorized.emailAddress,
          aContactNumber:
            form.values.aContactNumber.trim().length > 1
              ? form.values.aContactNumber
              : autofillData.representative.authorized.contactNumber ?? '',
        });

        nextStep();
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        let message = error.error || 'Failed to retrieve autofill data';
        showNotification({
          id: 'failed-to-retrieve-autofill-data',
          message,
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    retrieveAutofillData();
  }, []);

  return (
    <>
      <Helmet>
        <title>XPAY.World - Activate your account</title>
      </Helmet>
      <Container
        fluid
        sx={{
          '@media (min-width: 350px)': {
            padding: 0,
          },
          '@media (min-width: 650px)': {
            padding: 25,
          },
          '@media (min-width: 755px)': {
            padding: 50,
          },
        }}
      >
        <Grid className='merchant-onboarding'>
          <Grid.Col sm={12} lg={3} xl={2}>
            <Grid>
              <Grid.Col>
                <div className='titleHolder'>
                  <Text size='xl' className='title' weight={700}>
                    Activate your account
                  </Text>
                </div>
              </Grid.Col>
              <Grid.Col>
                <Paper
                  p={25}
                  shadow='md'
                  withBorder
                  style={{ maxWidth: '20rem' }}
                >
                  <Stepper
                    active={active}
                    onStepClick={setActive}
                    breakpoint={5000}
                    color='dark'
                  >
                    <Stepper.Step
                      label='Step 1'
                      description='Activation'
                      allowStepSelect={false}
                    ></Stepper.Step>
                    <Stepper.Step
                      label='Step 2'
                      description='Type of Business'
                      allowStepSelect={false}
                    ></Stepper.Step>
                    <Stepper.Step
                      label='Step 3'
                      description='General Information'
                      allowStepSelect={false}
                    ></Stepper.Step>
                    <Stepper.Step
                      label='Step 4'
                      description='Business Information'
                      allowStepSelect={false}
                    ></Stepper.Step>
                    <Stepper.Step
                      label='Step 5'
                      description='Statement of Acceptance'
                      allowStepSelect={false}
                    ></Stepper.Step>
                    <Stepper.Completed>Complete</Stepper.Completed>
                  </Stepper>
                </Paper>
              </Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col xs={0} sm={0} lg={1} xl={1}></Grid.Col>
          <Grid.Col sm={12} md={12} lg={8} xl={8}>
            <MediaQuery largerThan='lg' styles={{ paddingLeft: 0 }}>
              <Grid className='right-side-container'>
                <Grid.Col>
                  <MantineProvider
                    withGlobalStyles
                    withNormalizeCSS
                    theme={{
                      breakpoints: {
                        xs: 280,
                        sm: 400,
                        md: 500,
                        lg: 992,
                        xl: 1200,
                      },
                    }}
                  >
                    <div className={active === 0 ? 'show' : 'hide'}>
                      <ActivationStep
                        active={active}
                        prevStep={prevStep}
                        nextStep={nextStep}
                        isLoading={isLoading}
                      />
                    </div>
                    <div className={active === 1 ? 'show' : 'hide'}>
                      <BusinessTypeStep
                        mainForm={form}
                        active={active}
                        activated={active === 1}
                        prevStep={prevStep}
                        nextStep={nextStep}
                      />
                    </div>
                    <div className={active === 2 ? 'show' : 'hide'}>
                      <GeneralInformationStep
                        mainForm={form}
                        active={active}
                        activated={active === 2}
                        prevStep={prevStep}
                        nextStep={nextStep}
                      />
                    </div>

                    <div className={active === 3 ? 'show' : 'hide'}>
                      <BusinessFormStep
                        mainForm={form}
                        active={active}
                        activated={active === 3}
                        prevStep={prevStep}
                        nextStep={nextStep}
                      />
                    </div>
                    <div className={active === 4 ? 'show' : 'hide'}>
                      <StatementAcceptanceStep
                        mainForm={form}
                        active={active}
                        activated={active === 4}
                        prevStep={prevStep}
                      />
                    </div>
                  </MantineProvider>
                </Grid.Col>
                <Grid.Col xs={0} sm={0} lg={1} xl={1}></Grid.Col>
              </Grid>
            </MediaQuery>
          </Grid.Col>
        </Grid>
      </Container>
    </>
  );
}

export default ActivatePage;
